import {
  DownloadAndPurchaseButtonBlockProps,
  DownloadAndPurchaseButtonBlockStateProps
} from '../../types/components/products/DownloadAndPurchaseButtonBlock';
import { ApplicationState } from '../../types/state/storeTypes';
import { User } from '../../types/api/UsersTypes';
import { CollectionResponse } from '../../types/api/CollectionsIndexTypes';
import { CollectionsShowResponse } from '../../types/api/CollectionsShowTypes';
import { ProductResponse } from '../../types/api/ProductsIndexTypes';
import { CollectionOwnershipResponse } from '../../types/api/OwnershipTypes';
import { Subscription } from '../../types/pages/pricing/PricingPageTypes';

import PurchaseButton from './PurchaseButton';
import DownloadButton from './DownloadButton';

import { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import ownershipHelpers from '../../helpers/ownershipHelpers';
import { addMessage } from '../../state/actions/messagesActions';

import { isCreditBasedLegacy } from '../../helpers/subscriptionHelpers';
import { SmallCartIcon } from '../../modules/Icons';
import { checkLegitimateOwnership } from '../../helpers/licenseUtils';


const DownloadAndPurchaseButtonBlock: React.FC<DownloadAndPurchaseButtonBlockProps> = (props) => {
  const {
    mode,
    productItem,
    user,
    downloads: _downloads,
    singleProductCollectionDownload,
    onPurchased,
    // needed to load collection ownership object if productItem is a product within that collection
    // currently there isn't an API that allows to load just the product's ownership object
    collectionId,
    onOwnershipDataLoaded,
    subscription,
    setIsConfirmLicenseFlowOpen,
    setSelectedProduct,
    ownership,
    needsRepurchase,
    isPfl,
    setEmailVerificationModalOpen,
  } = props;
  const { getOwnershipFor, findOwnershipOf } = ownershipHelpers;
  const dispatch = useDispatch();

  const [itemPurchased, setItemPurchased] = useState(!!_downloads);
  const [downloads, setDownloads] = useState(_downloads);

  const [disableDownloadButton, setDisableDownloadButton] = useState<boolean>(false);

  useEffect(() => {
    const hasNonLegitimateLicense = checkLegitimateOwnership(ownership);
    setDisableDownloadButton(hasNonLegitimateLicense);
  }, [ownership]);


  const loadDownloads = async () => {
    try {
      const result = await getOwnershipFor(user, productItem, collectionId);
      const ownership = findOwnershipOf(
        productItem as ProductResponse,
        result as CollectionOwnershipResponse
      );
      if (onOwnershipDataLoaded) onOwnershipDataLoaded(ownership);
      const downloads = ownership.downloads;
      setItemPurchased(true);
      setDownloads(downloads);
    } catch (e) {
      dispatch(
        addMessage({
          type: 'error',
          body: e.message
        })
      );
    }
  };

  useEffect(() => {
    setItemPurchased(!!_downloads);
    setDownloads(_downloads);
  }, [_downloads]);

  const handlePurchasedItem = () => {
    loadDownloads();
    if (onPurchased) onPurchased();
  };

  const renderCartButton = (): React.ReactElement => {
    if (itemPurchased) return;
    if (!isCreditBasedLegacy(subscription as any)) return;
    return (
      <button className="mt-1 hover:stroke-a-blue" onClick={() => {
        if (setSelectedProduct) setSelectedProduct(productItem)
      }}>
        <SmallCartIcon />
      </button>
    );
  };

  const renderPurchaseButton = (): React.ReactElement => {
    // free subscribers and users with paid subscription should not see the Purchase Button
    // if it is a PFL product
    if (isPfl && (user?.free_subscriber || !!subscription?.active)) return;
    if (!isPfl && itemPurchased && !needsRepurchase) return;
    if (isCreditBasedLegacy(subscription as any)) return;
    return (
      <PurchaseButton
        mode={mode}
        purchasedItem={productItem as CollectionResponse | ProductResponse}
        onPurchased={handlePurchasedItem}
        setSelectedProduct={setSelectedProduct}
        collectionId={collectionId}
      />
    );
  };

  const renderDownloadButton = (): React.ReactElement => {
    // Users who are not free subscribers and do not have active subscriptions
    // should not be allowed to download PFL assets
    if (isPfl && !user?.free_subscriber && !subscription?.active) return;
    if (!itemPurchased) return;
    return (
      <DownloadButton
        mode={mode}
        downloadedItem={productItem as CollectionsShowResponse | ProductResponse}
        downloads={downloads}
        singleProductCollectionDownload={singleProductCollectionDownload}
        setIsConfirmLicenseFlowOpen={setIsConfirmLicenseFlowOpen}
        isPfl={isPfl}
        setEmailVerificationModalOpen={setEmailVerificationModalOpen}
        disableDownloadButton={disableDownloadButton}
        collectionId={collectionId}
      />
    );
  };

  return (
    <>
      {renderCartButton()}
      {renderPurchaseButton()}
      {!needsRepurchase && renderDownloadButton()}
    </>
  );
};

const mapStateToProps = (state: ApplicationState): DownloadAndPurchaseButtonBlockStateProps => ({
  user: state.auth.user as User,
  subscription: state.auth.subscription as Subscription
});

export default connect(mapStateToProps)(DownloadAndPurchaseButtonBlock);
